import React from 'react';

import {
  IconAward,
  IconCalendar,
  IconUserMinus,
  IconX,
} from '@tabler/icons-react';
import { Reassign } from 'icons';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/hooks/useAuth';

import PopoverMenuButton from '@/components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from '@/components/PopoverMenuButton/PopoverMenuButtonItem';
import { printTeamMember } from '@/components/print-report';
import { PrintCertificates } from '@/components/PrintCertificates';
import { PrintReportButton } from '@/components/PrintReportButton';

import { resetProgressModalOpenAtomFamily } from '../state';

interface ManageMenuProps {
  id: string;
  name: string;
  isComplete: boolean;
  isReports: boolean;
  isReportsPlansView: boolean;
  completedStepCount: number;
  handleReassign: () => void;
  handleUnassign: () => void;
  handleUpdateDueDate: () => void;
  handleMarkComplete: () => void;
  userAllowedToUpdateTMDueDateOnPlan?: boolean;
  userCanMarkAsComplete?: boolean;
}

const ResetProgressManageMenuItem: React.FC<{
  isInProgress?: boolean;
  teamMemberId: string;
}> = ({ isInProgress = false, teamMemberId }) => {
  const { t } = useTranslation();
  const setResetModalOpen = useSetAtom(
    resetProgressModalOpenAtomFamily(teamMemberId),
  );

  if (!isInProgress) {
    return null;
  }

  return (
    <PopoverMenuButtonItem
      data-testid="ResetProgress"
      icon={<IconX />}
      onClick={() => setResetModalOpen(true)}
      text={t('Button.resetProgress')}
      className="text-feedback-error-base!"
    />
  );
};

const ManageMenu: React.FC<ManageMenuProps> = ({
  id,
  name,
  isComplete,
  isReports,
  isReportsPlansView,
  completedStepCount,
  handleReassign,
  handleUnassign,
  handleUpdateDueDate,
  handleMarkComplete,
  userAllowedToUpdateTMDueDateOnPlan = false,
  userCanMarkAsComplete = true,
}) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();

  const locationsWithAtLeastLeaderRole =
    auth?.allLocationsWithAtLeastLeaderRoles ?? [];

  return (
    <div className="flex shrink-0 items-center justify-center gap-4">
      {isReports ? (
        <>
          {!isReportsPlansView && <PrintCertificates teamMemberId={id} />}
          <PrintReportButton
            onClick={e => {
              e.preventDefault();
              printTeamMember({ id, name });
            }}
          />
        </>
      ) : (
        <PopoverMenuButton
          data-testid="TeamMemberCardMenu"
          hideMenu={isReportsPlansView}
        >
          {isComplete && (
            <PopoverMenuButtonItem
              dataTestId="Reassign"
              icon={<Reassign />}
              onClick={() => handleReassign()}
              text={t('Button.reassign')}
            />
          )}
          <PopoverMenuButtonItem
            dataTestId="UnassignTeamMember"
            icon={<IconUserMinus />}
            onClick={handleUnassign}
            text={t('Button.unassign')}
          />
          {!isComplete && userAllowedToUpdateTMDueDateOnPlan && (
            <PopoverMenuButtonItem
              dataTestId="UpdateDueDate"
              icon={<IconCalendar />}
              onClick={handleUpdateDueDate}
              text={t('Button.updateDueDate')}
            />
          )}
          {!isComplete &&
            !!locationsWithAtLeastLeaderRole.length &&
            userCanMarkAsComplete && (
              <PopoverMenuButtonItem
                dataTestId="MarkCompleted"
                icon={<IconAward />}
                onClick={() => handleMarkComplete()}
                text={t('Button.markCompleted')}
              />
            )}
          <ResetProgressManageMenuItem
            isInProgress={completedStepCount > 0 && !isComplete}
            teamMemberId={id}
          />
        </PopoverMenuButton>
      )}
    </div>
  );
};

export default ManageMenu;
